import { useEffect, useState } from "react";
import api from "../lib/api";
import { runAiJob } from "../lib/aiJob";
import { toast } from "sonner";
import { Video, FileText, Sparkles, Send, PenLine, Check, ListTodo, ExternalLink, Users } from "lucide-react";

function MomBlock({ meeting, onChange }) {
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [summary, setSummary] = useState(meeting.mom?.summary || "");
  const [body, setBody] = useState(meeting.mom?.body || "");
  const [createTasks, setCreateTasks] = useState(true);

  const teamsTranscribed = meeting.meeting_link?.platform === "Teams" && meeting.transcription_enabled;
  const mom = meeting.mom;

  const gen = async () => {
    if (!teamsTranscribed && !notes.trim()) { toast.error("Enter post-meeting notes first"); return; }
    setBusy(true);
    try {
      await runAiJob(`/meetings/${meeting.id}/mom/generate`, { notes: notes.trim() || null });
      toast.success("MOM drafted — review before sending");
      onChange();
    } catch (e) { toast.error(e.response?.data?.detail || e.message || "Failed"); }
    finally { setBusy(false); }
  };
  const saveEdit = async () => {
    setBusy(true);
    try { await api.patch(`/meetings/${meeting.id}/mom`, { summary, body }); toast.success("Draft updated"); setEditing(false); onChange(); }
    catch { toast.error("Failed"); } finally { setBusy(false); }
  };
  const approve = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/meetings/${meeting.id}/mom/approve`, { create_tasks: createTasks });
      toast.success(`MOM emailed to ${r.data.emailed} participant(s)${r.data.tasks_created ? ` · ${r.data.tasks_created} tasks created` : ""}`);
      onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (!mom) {
    return (
      <div className="mt-3 pt-3 border-t border-[#DFDAD0]" data-testid={`mom-gen-${meeting.id}`}>
        <div className="font-mono text-[10px] uppercase text-[#1C2530]/60 mb-2">Meeting Intelligence · draft MOM</div>
        {teamsTranscribed ? (
          <div className="font-mono text-[11px] text-[#3E8F99] mb-2 flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> Teams transcript available via Graph — will draft from transcript</div>
        ) : (
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid={`mom-notes-${meeting.id}`}
            placeholder="Quick post-meeting notes (any platform / Teams without transcription)…"
            className="w-full border border-[#DFDAD0] px-2 py-1.5 text-xs h-20 outline-none focus:border-[#1C2530] mb-2" />
        )}
        <button onClick={gen} disabled={busy} data-testid={`mom-generate-${meeting.id}`}
          className="flex items-center gap-2 border border-[#DFDAD0] px-3 py-1.5 text-xs font-mono uppercase hover:bg-[#EFECE5] disabled:opacity-40">
          <Sparkles className="w-3.5 h-3.5" /> Generate MOM {teamsTranscribed ? "from transcript" : "from notes"}
        </button>
      </div>
    );
  }

  return (
    <div className="mt-3 pt-3 border-t border-[#DFDAD0]" data-testid={`mom-${meeting.id}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="font-mono text-[10px] uppercase text-[#1C2530]/60">MOM · source: {mom.source}</div>
        <span className="font-mono text-[9px] uppercase px-1 py-0.5 border"
          style={{ color: mom.status === "sent" ? "#2F7A3E" : "#E2711D", borderColor: mom.status === "sent" ? "#2F7A3E" : "#E2711D" }}>
          {mom.status === "sent" ? `SENT · ${mom.approved_by}` : "DRAFT · needs review"}
        </span>
      </div>
      {editing ? (
        <>
          <input value={summary} onChange={(e) => setSummary(e.target.value)} className="w-full border border-[#DFDAD0] px-2 py-1 text-xs mb-2 outline-none focus:border-[#1C2530]" />
          <textarea value={body} onChange={(e) => setBody(e.target.value)} data-testid={`mom-body-${meeting.id}`} className="w-full border border-[#DFDAD0] px-2 py-1.5 text-xs h-32 outline-none focus:border-[#1C2530]" />
          <button onClick={saveEdit} disabled={busy} className="mt-2 flex items-center gap-1 border border-[#DFDAD0] px-2 py-1 font-mono text-[10px] uppercase hover:bg-[#EFECE5]"><Check className="w-3 h-3" /> Save</button>
        </>
      ) : (
        <>
          <div className="text-xs text-[#1C2530]/80 font-medium mb-1">{mom.summary}</div>
          <div className="text-xs text-[#1C2530]/70 whitespace-pre-wrap border-l-2 border-[#DFDAD0] pl-2 max-h-40 overflow-auto">{mom.body}</div>
          {mom.action_items?.length > 0 && (
            <div className="mt-2">
              <div className="font-mono text-[10px] uppercase text-[#1C2530]/50 mb-1">Action items</div>
              {mom.action_items.map((a, i) => (
                <div key={i} className="text-xs text-[#1C2530]/80 flex items-start gap-1.5"><ListTodo className="w-3 h-3 mt-0.5 shrink-0 text-[#3E8F99]" /> {typeof a === "string" ? a : `${a.title}${a.owner ? ` — ${a.owner}` : ""}${a.due ? ` (due ${a.due})` : ""}`}</div>
              ))}
            </div>
          )}
        </>
      )}
      {mom.status === "draft" && !editing && (
        <div className="flex flex-wrap items-center gap-3 mt-3">
          <button onClick={() => setEditing(true)} data-testid={`mom-edit-${meeting.id}`} className="flex items-center gap-1 border border-[#DFDAD0] px-2 py-1 font-mono text-[10px] uppercase hover:bg-[#EFECE5]"><PenLine className="w-3 h-3" /> Edit</button>
          <label className="flex items-center gap-1.5 font-mono text-[10px] uppercase text-[#1C2530]/70 cursor-pointer">
            <input type="checkbox" checked={createTasks} onChange={(e) => setCreateTasks(e.target.checked)} data-testid={`mom-create-tasks-${meeting.id}`} /> create tasks
          </label>
          <button onClick={approve} disabled={busy} data-testid={`mom-approve-${meeting.id}`} className="flex items-center gap-1 bg-[#1C2530] text-[#F5F3EE] px-2 py-1 font-mono text-[10px] uppercase hover:bg-[#2a3644]"><Send className="w-3 h-3" /> Approve & Email all participants</button>
        </div>
      )}
    </div>
  );
}

export default function MeetingsPanel({ leadId, refreshKey }) {
  const [meetings, setMeetings] = useState(null);
  const [tasks, setTasks] = useState([]);

  const load = async () => {
    const [m, t] = await Promise.all([api.get(`/leads/${leadId}/meetings`), api.get(`/leads/${leadId}/tasks`)]);
    setMeetings(m.data); setTasks(t.data);
  };
  useEffect(() => { load(); }, [leadId, refreshKey]);

  return (
    <div className="border border-[#DFDAD0] bg-white p-5" data-testid="meetings-panel">
      <div className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60 mb-3">Meetings & Minutes</div>
      {!meetings ? <div className="font-mono text-xs text-[#1C2530]/40">SYS_LOADING...</div>
        : meetings.length === 0 ? <div className="font-mono text-xs text-[#1C2530]/40">NO_MEETINGS · book one above</div>
        : meetings.map((m) => (
          <div key={m.id} className="border border-[#DFDAD0] p-3 mb-3" data-testid={`meeting-${m.id}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Video className="w-4 h-4 text-[#1C2530]/50" strokeWidth={1.5} />
                <span className="text-sm font-medium text-[#1C2530]">{m.meeting_link?.platform}</span>
                <span className="font-mono text-[11px] text-[#1C2530]/60">{new Date(m.slot).toLocaleString()}</span>
                {m.transcription_enabled && m.meeting_link?.platform === "Teams" && <span className="font-mono text-[9px] uppercase border border-[#3E8F99] text-[#3E8F99] px-1">transcribed</span>}
              </div>
              <a href={m.meeting_link?.url} target="_blank" rel="noreferrer" className="flex items-center gap-1 font-mono text-[10px] text-[#3E8F99] hover:underline"><ExternalLink className="w-3 h-3" /> join link</a>
            </div>
            <div className="flex items-center gap-1.5 mt-1 font-mono text-[10px] text-[#1C2530]/50">
              <Users className="w-3 h-3" /> {(m.participants || []).map((p) => `${p.name} (${p.type})`).join(" · ")}
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              <button onClick={async () => { try { await api.post(`/meetings/${m.id}/send-reminder`); toast.success("Reminder sent"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } }} data-testid={`remind-${m.id}`} className="border border-[#DFDAD0] px-2 py-1 font-mono text-[10px] uppercase hover:bg-[#EFECE5]">Send reminder</button>
              <button onClick={async () => { try { await api.post(`/meetings/${m.id}/no-show`); toast.success("No-show + reschedule offer"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } }} data-testid={`noshow-${m.id}`} className="border border-[#B23A2E]/40 text-[#B23A2E] px-2 py-1 font-mono text-[10px] uppercase hover:bg-[#B23A2E]/10">No-show</button>
              {m.reminders?.first && <span className="font-mono text-[9px] text-[#2F7A3E] self-center">reminder 1 ✓</span>}
              {m.reminders?.second && <span className="font-mono text-[9px] text-[#2F7A3E] self-center">reminder 2 ✓</span>}
              {m.no_show && <span className="font-mono text-[9px] text-[#B23A2E] self-center">NO-SHOW · rescheduled</span>}
            </div>
            <MomBlock meeting={m} onChange={load} />
          </div>
        ))}

      {tasks.length > 0 && (
        <div className="mt-2 pt-3 border-t border-[#DFDAD0]" data-testid="tasks-list">
          <div className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60 mb-2">Tasks (from MOM action items)</div>
          {tasks.map((t) => (
            <div key={t.id} className="flex items-center gap-2 text-xs text-[#1C2530]/80 py-1" data-testid={`task-${t.id}`}>
              <ListTodo className="w-3.5 h-3.5 text-[#3E8F99]" /> {t.title}
              {t.due && <span className="font-mono text-[10px] text-[#1C2530]/50">· due {t.due}</span>}
              <span className="font-mono text-[9px] uppercase border border-[#DFDAD0] px-1 text-[#1C2530]/50 ml-auto">{t.status}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
